import React from 'react';
import { motion } from 'framer-motion';
import { Truck, MapPin, Clock, Phone } from 'lucide-react';

const Pickup: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-cream-50 to-premium-50">
      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-to-r from-burgundy-100 via-cream-100 to-premium-100">
        <div className="container mx-auto px-4 safe-area-inset">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-5xl md:text-6xl font-serif font-bold text-gray-900 mb-6">
              Pickup Policy
            </h1>
            <div className="w-24 h-1 gold-gradient rounded-full mx-auto mb-6"></div>
            <p className="text-xl text-gray-600 leading-relaxed">
              Learn about our pickup service, timing, and policies for collecting your custom cakes at our Seminyak location.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-20">
        <div className="container mx-auto px-4 safe-area-inset">
          <div className="max-w-4xl mx-auto space-y-8">
            {[
              {
                icon: MapPin,
                title: "Pickup Location",
                content: "All cakes are available for pickup at our store location in Seminyak, Bali. We do not offer delivery service. Our address: Jl. Dewi Saraswati No.9, Seminyak, Bali, Indonesia. Easy parking available on-site."
              },
              {
                icon: Clock,
                title: "Pickup Hours",
                content: "Pickup available Monday-Friday 9:00 AM - 6:00 PM, Saturday 9:00 AM - 4:00 PM, and Sunday by appointment only. Please schedule your pickup time when placing your order to ensure your cake is ready."
              },
              {
                icon: Truck,
                title: "Pickup Process",
                content: "Your cake will be carefully packaged in our specialized cake boxes for safe transport. We'll provide handling instructions and transportation tips. Please bring your order confirmation and arrive within your scheduled pickup window."
              },
              {
                icon: Phone,
                title: "Special Instructions",
                content: "Please confirm your pickup time 24 hours in advance. Bring a valid ID and order confirmation. For large or multi-tier cakes, we recommend bringing an extra person to help with transport. Cold storage tips will be provided for longer journeys."
              }
            ].map((section, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="premium-card bento-card"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-burgundy-400 to-burgundy-600 rounded-xl flex items-center justify-center flex-shrink-0">
                    <section.icon className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-serif font-semibold text-gray-900 mb-4">
                      {section.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {section.content}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Delivery Areas Map */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 safe-area-inset">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-serif font-bold text-gray-900 mb-6">
              Store Location & Directions
            </h2>
            <div className="w-24 h-1 gold-gradient rounded-full mx-auto"></div>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { zone: "Main Address", distance: "Jl. Dewi Saraswati No.9", price: "Seminyak, Bali", color: "from-burgundy-400 to-burgundy-600" },
              { zone: "Parking Available", distance: "On-site parking", price: "Free parking", color: "from-green-400 to-green-600" },
              { zone: "Landmarks Nearby", distance: "Near Potato Head", price: "Easy to find", color: "from-blue-400 to-blue-600" }
            ].map((location, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="premium-card bento-card text-center"
              >
                <div className={`w-16 h-16 mx-auto mb-4 bg-gradient-to-br ${location.color} rounded-2xl flex items-center justify-center`}>
                  <MapPin className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-serif font-semibold text-gray-900 mb-2">
                  {location.zone}
                </h3>
                <p className="text-gray-600 mb-2">{location.distance}</p>
                <p className="text-lg font-semibold text-burgundy-600">{location.price}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Pickup;
